import React, { useEffect, useMemo, useState } from "react";
import {
	Badge,
	Box,
	Button,
	ButtonGroup,
	Divider,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { ImageViewer } from "../ImageViewer";
import { useGlobalSnackBar } from "../../pages/SnackbarContext";
import { Option } from "../../utils/option";

import "./styles.css";
import { DimensionsContext } from "./DimensionsContext";

const DRAWER_WIDTH = 256;
const HEADER_HEIGHT = 64;

const generateFullUncompatibilityOptionList = (data: Option[]) => {
	return data.map((item: Option) => {
		const uncompatibleIds = item.uncompatible_option_ids
			? JSON.parse(item.uncompatible_option_ids)
			: [];
		data.forEach((otherItem) => {
			const otherItemUncompatibleIds = otherItem.uncompatible_option_ids
				? JSON.parse(otherItem.uncompatible_option_ids)
				: [];
			if (
				otherItem.ID !== item.ID &&
				otherItemUncompatibleIds.includes(item.ID)
			) {
				if (!uncompatibleIds.includes(otherItem.ID)) {
					uncompatibleIds.push(otherItem.ID);
				}
			}
		});
		return { ...item, uncompatible_ids: uncompatibleIds };
	});
};

const considerUnCompatibleData = (data: Option[], selectedId: string) => {
	// Clone the data to avoid modifying the original array
	let updatedData = data.map((item) => ({ ...item }));

	// Find the selected item
	let selectedItem = updatedData.find((item) => item.ID === selectedId);
	if (selectedItem) {
		// Toggle isAdded status
		selectedItem.isAdded = !selectedItem.isAdded;
	}

	// Recalculate the disabled status for all items
	updatedData.forEach((item) => {
		item.disabled = false;
	});

	// Find all added items and mark their incompatible items as disabled
	updatedData.forEach((item) => {
		if (item.isAdded) {
			item?.uncompatible_ids?.forEach((uncompatibleId: string) => {
				let uncompatibleItem = updatedData.find(
					(ui) => ui.ID === uncompatibleId
				);
				if (uncompatibleItem) {
					uncompatibleItem.disabled = true;
					return;
				}
			});
		}
	});

	return updatedData;
};

export const CustomizeOptions: React.FC<any> = ({
	options,
	categories,
	setOrderedOptions,
	handleFinish,
	activeQuoteModel,
}) => {
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [isSinglePage, setIsSinglePage] = useState<boolean>(false);
	const [drawerOpen, setDrawerOpen] = useState<boolean>(true);

	const { showSnackBar } = useGlobalSnackBar();

	const matchIDs = useMemo(() => {
		return activeQuoteModel?.applicable_option_ids
			? JSON.parse(activeQuoteModel.applicable_option_ids)
			: [];
	}, [activeQuoteModel?.applicable_option_ids]);

	const applicableOptions = useMemo(() => {
		return options?.filter((item: Option) => matchIDs.includes(item.ID));
	}, [options, matchIDs]);

	const totalPages = useMemo(() => {
		return categories?.length;
	}, [categories]);

	const isIncludedItems = useMemo(() => {
		return !categories
			?.slice(currentPage)
			.flatMap((item: any) => item.items)
			.some((item: Option) => matchIDs.includes(item.ID));
	}, [categories, currentPage, matchIDs]);

	const currentData = useMemo(() => {
		return applicableOptions?.filter(
			(res: Option) => res.parent_id === categories[currentPage - 1].ID
		);
	}, [applicableOptions, currentPage, categories]);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const generateFullUncompatibilityOptions = useMemo(() => {
		return generateFullUncompatibilityOptionList(applicableOptions);
	}, [applicableOptions]);

	const handlePrev = () => {
		let prevPage = currentPage - 1;
		while (
			prevPage > 0 &&
			!applicableOptions?.some(
				(option: Option) => option.parent_id === categories[prevPage - 1].ID
			)
		) {
			prevPage--;
		}

		if (prevPage > 0) {
			setCurrentPage(prevPage);
		}
	};

	const handleNext = () => {
		let nextPage = currentPage + 1;
		while (
			nextPage <= totalPages &&
			!applicableOptions?.some(
				(option: Option) => option.parent_id === categories[nextPage - 1].ID
			)
		) {
			nextPage++;
		}

		if (nextPage <= totalPages) {
			setCurrentPage(nextPage);
		} else {
			showSnackBar(`Added options.`);
			handleFinish();
		}
	};

	useEffect(() => {
		let initialPage = 1;
		let nonEmptyCount = 0;

		while (
			initialPage <= totalPages &&
			!applicableOptions?.some(
				(option: Option) => option.parent_id === categories[initialPage - 1].ID
			)
		) {
			initialPage++;
		}

		if (initialPage <= totalPages) {
			setCurrentPage(initialPage);
		}

		// Count non-empty categories
		categories.forEach((_: any, index: number) => {
			if (
				applicableOptions?.some(
					(option: Option) => option.parent_id === categories[index].ID
				)
			) {
				nonEmptyCount++;
			}
		});

		// If only one non-empty category, set isSinglePage to true
		setIsSinglePage(nonEmptyCount === 1);
	}, [totalPages]);

	const handleChoseOption = (option: any, isAdded: boolean) => {
		const updatedData = considerUnCompatibleData(
			generateFullUncompatibilityOptions,
			option.ID
		);

		if (isAdded) {
			showSnackBar(`Added ${option?.option_name}.`);
		} else {
			showSnackBar(`Removed ${option?.option_name}.`, "error");
		}
		setOrderedOptions(updatedData);
	};

	const selectQTY = (number: number, option: Option) => {
		let updatedData = generateFullUncompatibilityOptions.map((item) => ({ ...item }));

		// Find the selected item
		let selectedItem = updatedData.find((item) => item.ID === option?.ID);
		if (selectedItem) {
			// Toggle isAdded status
			selectedItem.QTYNumber = Number(number);
		}

		setOrderedOptions(updatedData);
	}

	const handleSubtract = (option: Option) => {
		let number: number = (option?.QTYNumber || 1) - 1;
		if (number < 1) {
			number = 1;
			showSnackBar(`Minimum QTY 1.`, "error");
		}
		selectQTY(number, option)
	};

	const handleAdd = (option: Option) => {
		let number: number = (option?.QTYNumber || 1) + 1;
		if (number > (option?.QTY || 1)) {
			number = (option?.QTY || 1);
			showSnackBar(`Maximum QTY ${option?.QTY || 1}.`, "error");
		}
		selectQTY(number, option)
	};

	const getCategoryName = useMemo(() => {
		return categories[currentPage - 1]?.option_name;
	}, [categories, currentPage]);

	// Build summary calculations
	const selectedOptions = useMemo(() => {
		return applicableOptions?.filter((opt: Option) => opt.isAdded) ?? [];
	}, [applicableOptions]);

	const basePrice = activeQuoteModel?.dealer_price ?? 0;

	const optionsTotalPrice = useMemo(() => {
		return selectedOptions.reduce((sum: number, opt: Option) => {
			const qty = opt.QTYNumber || 1;
			const price = opt.dealer_price || 0;
			return sum + price * qty;
		}, 0);
	}, [selectedOptions]);

	const runningTotal = basePrice + optionsTotalPrice;

	// Non-empty categories for the stepper (with their original index for navigation)
	const nonEmptyCategories = useMemo(() => {
		return categories
			?.map((cat: any, index: number) => ({ cat, originalIndex: index }))
			.filter(({ cat }: any) =>
				applicableOptions?.some((opt: Option) => opt.parent_id === cat.ID)
			) ?? [];
	}, [categories, applicableOptions]);

	// Selected count per category
	const selectedCountByCategory = useMemo(() => {
		const counts: Record<string, number> = {};
		nonEmptyCategories.forEach(({ cat }: any) => {
			counts[cat.ID] = applicableOptions?.filter(
				(opt: Option) => opt.parent_id === cat.ID && opt.isAdded
			).length ?? 0;
		});
		return counts;
	}, [nonEmptyCategories, applicableOptions]);

	// Index of the active tab in nonEmptyCategories (used for progress arrow)
	const activeIdx = useMemo(() => {
		return nonEmptyCategories.findIndex(
			(c: any) => (c.originalIndex + 1) === currentPage
		);
	}, [nonEmptyCategories, currentPage]);

	return (
		<>
			{applicableOptions?.length ? (
				<>
					{/* Category Navigation — strip on desktop, dropdown on mobile */}
					{isMobile ? (
						<Box sx={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#FFFFFF", borderBottom: "1px solid #E2E8F0", mb: 3, p: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
							<Select
								value={currentPage}
								onChange={(e) => setCurrentPage(Number(e.target.value))}
								fullWidth
								size="small"
								sx={{
									backgroundColor: "#F8FAFC",
									color: "#0B1929",
									borderRadius: 1,
									"& .MuiSelect-select": { py: 1 },
								}}
							>
								{nonEmptyCategories.map(({ cat, originalIndex }: any) => {
									const page = originalIndex + 1;
									const count = selectedCountByCategory[cat.ID] || 0;
									return (
										<MenuItem key={cat.ID} value={page}>
											{count > 0 ? `✓ ${cat.option_name} (${count})` : cat.option_name}
										</MenuItem>
									);
								})}
							</Select>
						</Box>
					) : (
						<Box
							sx={{
								position: "sticky",
								top: 0,
								zIndex: 10,
								backgroundColor: "#FFFFFF",
								display: "flex",
								overflow: "visible",
								mb: 3,
							}}
						>
							{nonEmptyCategories.map(({ cat, originalIndex }: any, idx: number) => {
								const page = originalIndex + 1;
								const isActive = page === currentPage;
								const isPast = idx < activeIdx;
								const isNextAfterActive = idx === activeIdx + 1;
								const count = selectedCountByCategory[cat.ID] || 0;

								return (
									<Box
										key={cat.ID}
										onClick={() => setCurrentPage(page)}
										sx={{
											flex: 1,
											py: 1.5,
											px: 1.5,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 0.5,
											cursor: "pointer",
											position: "relative",
											zIndex: isActive ? 2 : 1,
											color: isActive || isPast ? "#0B1929" : "#64748B",
											fontWeight: isActive ? 600 : 400,
											fontSize: "0.875rem",
											transition: "all 0.2s ease",
											borderBottom: (isActive || isPast) ? "none" : "3px solid #E2E8F0",
											borderRight: idx < nonEmptyCategories.length - 1 ? "1px solid #E2E8F0" : "none",
											"&:hover": {
												backgroundColor: "#F8FAFC",
												color: "#1E293B",
											},
											// Cyan line on past and active tabs
											...((isActive || isPast) ? {
												"&::before": {
													content: '""',
													position: "absolute",
													bottom: 0,
													left: -1,
													right: -1,
													height: "3px",
													backgroundColor: "#00B4D8",
													zIndex: 3,
												},
											} : isNextAfterActive ? {
												// White mask on the tab after active — covers the vertical divider behind the arrow
												"&::before": {
													content: '""',
													position: "absolute",
													left: -10,
													bottom: -5,
													width: 0,
													height: 0,
													borderTop: "7px solid transparent",
													borderBottom: "7px solid transparent",
													borderLeft: "10px solid #FFFFFF",
													zIndex: 3,
												},
											} : {}),
											// Arrowhead only on the active tab (the leading edge)
											...(isActive && idx < nonEmptyCategories.length - 1 ? {
												"&::after": {
													content: '""',
													position: "absolute",
													right: -8,
													bottom: -4,
													width: 0,
													height: 0,
													borderTop: "5.5px solid transparent",
													borderBottom: "5.5px solid transparent",
													borderLeft: "8px solid #00B4D8",
													zIndex: 4,
												},
											} : {}),
										}}
									>
										{count > 0 && (
											<CheckCircleIcon sx={{ fontSize: 16, color: "#10B981" }} />
										)}
										{cat.option_name}
										{count > 0 && (
											<span style={{ color: "#10B981", fontSize: "0.75rem", marginLeft: 4 }}>({count})</span>
										)}
									</Box>
								);
							})}
						</Box>
					)}

					{/* Option cards — full width, consistent layout */}
					<Grid container spacing={2} sx={{ minHeight: 300 }}>
						{currentData && currentData.length ? (
							currentData?.map((option: Option) => (
								<Grid item xs={12} sm={6} key={option.ID} sx={{ maxWidth: { sm: "50%" } }}>
									<Paper
										variant="outlined"
										sx={{
											p: 2,
											opacity: option?.disabled ? 0.5 : 1,
											pointerEvents: option?.disabled ? "none" : "auto",
											border: "1px solid #E2E8F0",
											borderRadius: 2,
											transition: "all 0.2s ease",
											"&:hover": {
												borderColor: "#00B4D8",
												boxShadow: "0 4px 12px rgba(0, 180, 216, 0.1)",
											},
										}}
									>
										{/* Image */}
										<Box sx={{ mb: 1.5 }}>
											<ImageViewer
												images={
													option.images
														? JSON.parse(option.images)?.[0]
															? [JSON.parse(option.images)?.[0]]
															: []
														: []
												}
												height={120}
											/>
										</Box>

										{/* Option name */}
										<Typography variant="subtitle1" fontWeight={600} gutterBottom>
											{option?.option_name}
										</Typography>

										{/* Pricing */}
										<Box sx={{ display: "flex", gap: 2, mb: 1 }}>
											<Typography variant="body2" color="text.secondary">
												MSRP: ${option.MSRP?.toLocaleString() ?? "0"}
											</Typography>
											<Typography variant="body2" color="text.secondary">
												{process.env.REACT_APP_DEALER_NAME}: ${option.dealer_price?.toLocaleString() ?? "0"}
											</Typography>
										</Box>

										{/* Dimensions */}
										<DimensionsContext option={option} />

										{/* Unified Add/QTY control */}
										{!option?.isAdded ? (
											<Button
												variant="contained"
												size="small"
												fullWidth
												disabled={option?.disabled}
												onClick={() => handleChoseOption(option, true)}
												sx={{ mt: 1 }}
											>
												Add Option
											</Button>
										) : option?.isQTYEnabled ? (
											<ButtonGroup
												fullWidth
												size="small"
												variant="contained"
												sx={{ mt: 1 }}
											>
												<Button
													color="error"
													onClick={() => {
														if ((option?.QTYNumber || 1) <= 1) {
															handleChoseOption(option, false);
														} else {
															handleSubtract(option);
														}
													}}
													sx={{ minWidth: 40, flexGrow: 0 }}
												>
													-
												</Button>
												<Button
													disabled
													sx={{
														flexGrow: 1,
														color: "text.primary !important",
														bgcolor: "grey.100 !important",
														borderColor: "divider !important",
														fontSize: "0.85rem",
													}}
												>
													Qty: {option?.QTYNumber || 1}
												</Button>
												<Button
													color="primary"
													onClick={() => handleAdd(option)}
													sx={{ minWidth: 40, flexGrow: 0 }}
												>
													+
												</Button>
											</ButtonGroup>
										) : (
											<Button
												variant="outlined"
												color="error"
												size="small"
												fullWidth
												onClick={() => handleChoseOption(option, false)}
												sx={{ mt: 1 }}
											>
												Remove
											</Button>
										)}
									</Paper>
								</Grid>
							))
						) : (
							<Grid item xs={12}>
								<Typography variant="h6" align="center" sx={{ py: 4 }}>
									<i>Empty Category</i>
								</Typography>
							</Grid>
						)}
					</Grid>

					{/* Pagination */}
					<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3, mb: 3 }}>
						<Button
							variant="contained"
							onClick={handlePrev}
							disabled={currentPage === 1}
						>
							Prev
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleNext}
							disabled={!categories?.length}
						>
							{isSinglePage || currentPage === totalPages || isIncludedItems
								? "Finish"
								: "Next"}
						</Button>
					</Box>
				</>
			) : (
				<>
					<Typography variant="h5" align="center" sx={{ mb: 2, mt: 5 }}>
						<i>No Available Option</i>
					</Typography>

					<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3, mb: 3 }}>
						<Button variant="contained" color="primary" onClick={handleNext}>
							Finish
						</Button>
					</Box>
				</>
			)}

			{/* Floating Build Summary Drawer */}
			<Box
				sx={{
					position: "fixed",
					right: 0,
					top: HEADER_HEIGHT,
					height: `calc(100vh - ${HEADER_HEIGHT}px)`,
					width: DRAWER_WIDTH,
					backgroundColor: "#fff",
					boxShadow: "-4px 0 12px rgba(0,0,0,0.08)",
					borderLeft: "1px solid #E2E8F0",
					zIndex: 1200,
					transform: drawerOpen ? "translateX(0)" : `translateX(${DRAWER_WIDTH}px)`,
					transition: "transform 0.3s ease",
					display: "flex",
					flexDirection: "column",
				}}
			>
				{/* Toggle tab */}
				<Box
					onClick={() => setDrawerOpen((prev) => !prev)}
					sx={{
						position: "absolute",
						left: -40,
						top: 16,
						width: 40,
						height: 48,
						backgroundColor: "#fff",
						borderRadius: "8px 0 0 8px",
						boxShadow: "-4px 0 8px rgba(0,0,0,0.08)",
						borderLeft: "1px solid #E2E8F0",
						borderTop: "1px solid #E2E8F0",
						borderBottom: "1px solid #E2E8F0",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
						"&:hover": {
							backgroundColor: "#f5f5f5",
						},
					}}
				>
					{drawerOpen ? (
						<ChevronRightIcon fontSize="small" />
					) : (
						<Badge
							badgeContent={selectedOptions.length}
							color="secondary"
							max={99}
						>
							<ReceiptIcon fontSize="small" />
						</Badge>
					)}
				</Box>

				{/* Drawer content */}
				<Box
					sx={{
						flex: 1,
						overflowY: "auto",
						p: 2,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Typography variant="h6" gutterBottom>
						Build Summary
					</Typography>
					<Divider sx={{ mb: 1.5 }} />

					{/* Base model price */}
					<Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
						<Typography variant="body2" sx={{ flex: 1, wordBreak: "break-word", mr: 1 }}>
							{activeQuoteModel?.model_name ?? "Base Model"}
						</Typography>
						<Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
							${basePrice.toLocaleString()}
						</Typography>
					</Box>

					<Divider sx={{ my: 1 }} />

					{/* Selected options list */}
					{selectedOptions.length > 0 ? (
						selectedOptions.map((opt: Option) => {
							const qty = opt.QTYNumber || 1;
							const lineTotal = (opt.dealer_price || 0) * qty;
							return (
								<Box
									key={opt.ID}
									sx={{
										display: "flex",
										alignItems: "flex-start",
										mb: 0.75,
										gap: 0.5,
									}}
								>
									{/* Option name — full text, wrapping */}
									<Typography
										variant="body2"
										sx={{ flex: 1, wordBreak: "break-word", lineHeight: 1.4 }}
									>
										{opt.option_name}
									</Typography>

									{/* Qty — only if > 1 */}
									{opt.isQTYEnabled && qty > 1 && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ whiteSpace: "nowrap", pt: 0.2 }}
										>
											x{qty}
										</Typography>
									)}

									{/* Price */}
									<Typography
										variant="body2"
										sx={{ whiteSpace: "nowrap", textAlign: "right", minWidth: 60 }}
									>
										${lineTotal.toLocaleString()}
									</Typography>

									{/* Remove button */}
									<IconButton
										size="small"
										onClick={() => handleChoseOption(opt, false)}
										sx={{ p: 0.25, ml: 0.25 }}
									>
										<CloseIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</Box>
							);
						})
					) : (
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							No options selected
						</Typography>
					)}

					{/* Spacer to push total to bottom */}
					<Box sx={{ flex: 1 }} />

					{/* Running total */}
					<Divider sx={{ mt: 1.5, mb: 1 }} />
					<Box sx={{ display: "flex", justifyContent: "space-between" }}>
						<Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "1.1rem" }}>
							Total
						</Typography>
						<Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "1.1rem" }}>
							${runningTotal.toLocaleString()}
						</Typography>
					</Box>
				</Box>
			</Box>
		</>
	);
};
